/**
 * LinkPickerDialog
 *
 * Finding #8 — when the Scripts-panel context menu triggers Refresh from DB
 * (or Apply folder to DB) for a multi-link blueprint folder AND the user has
 * no open Database Blueprint tab to read a selection from, we must NOT silently
 * pick `linkedDbs[0]`. This dialog asks the user which linked DB to act
 * against, with the most-recently-synced link preselected as a sensible
 * default.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { relativeTime } from '../../../../core/utils/relativeTime';
import type { LinkedDb } from '../../../database-blueprint/types/databaseBlueprintTypes';

export type LinkPickerLink = Pick<
  LinkedDb,
  'label' | 'connectionDisplayName' | 'database' | 'engine' | 'lastSyncAt'
>;

type LinkPickerDialogProps = {
  open: boolean;
  links: ReadonlyArray<LinkPickerLink>;
  /** Pre-selected label (e.g. the active blueprint tab's selection). */
  initialLabel?: string;
  /** Action label, e.g. 'Refresh' or 'Apply'. */
  actionLabel: string;
  /** Title text. */
  title: string;
  /** Body text describing what the action will do. */
  description: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: (label: string) => void;
};

function pickInitialLabel(
  links: ReadonlyArray<LinkPickerLink>,
  initialLabel: string | undefined,
): string {
  if (initialLabel && links.some((l) => l.label === initialLabel)) {
    return initialLabel;
  }
  // Prefer the most-recently-synced link — a safer default than `[0]`.
  let best: { label: string; ts: number } | null = null;
  for (const link of links) {
    const ts = link.lastSyncAt ? Date.parse(link.lastSyncAt) : 0;
    if (!best || ts > best.ts) best = { label: link.label, ts };
  }
  return best?.label ?? links[0]?.label ?? '';
}

export function LinkPickerDialog({
  open,
  links,
  initialLabel,
  actionLabel,
  title,
  description,
  onOpenChange,
  onConfirm,
}: LinkPickerDialogProps) {
  const [selectedLabel, setSelectedLabel] = useState<string>(() =>
    pickInitialLabel(links, initialLabel),
  );

  useEffect(() => {
    if (!open) return;
    setSelectedLabel(pickInitialLabel(links, initialLabel));
  }, [open, links, initialLabel]);

  const handleConfirm = () => {
    if (!selectedLabel) return;
    onConfirm(selectedLabel);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="database-blueprint-link-picker">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={selectedLabel}
          onValueChange={setSelectedLabel}
          className="space-y-1.5"
        >
          {links.map((link) => (
            <div
              key={link.label}
              className="flex items-start gap-3 rounded-sm border px-3 py-2 hover:bg-accent/40"
              data-testid={`database-blueprint-link-picker-option-${link.label}`}
            >
              <RadioGroupItem
                id={`database-blueprint-link-picker-${link.label}`}
                value={link.label}
                className="mt-0.5"
              />
              <Label
                htmlFor={`database-blueprint-link-picker-${link.label}`}
                className="flex flex-col gap-0.5 flex-1 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium">{link.label}</span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {link.engine}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  {link.connectionDisplayName} · {link.database}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Last synced: {relativeTime(link.lastSyncAt, { nullLabel: 'never' })}
                </span>
              </Label>
            </div>
          ))}
        </RadioGroup>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="database-blueprint-link-picker-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedLabel}
            data-testid="database-blueprint-link-picker-confirm"
          >
            {actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
